import { registerChatSessionClearer } from '@/slices/chat/chatSessionLifecycle';
import { claimMediaFocus, registerMediaFocusOwner } from '@/utils/mediaFocus';

import {
  createNativeMediaSession,
  getNativeSessionMarkerStorage,
  hasNativeSessionMarker,
} from './nativeMediaSession';

/**
 * The playback engine for Audio Overviews (ORA-735): one long-lived media
 * element that keeps playing while the student moves around the app, locks the
 * phone or switches tabs.
 *
 * Unlike chat read-aloud (`chatAudio.ts`), nothing here pauses on visibility
 * or app-state changes: background listening is the point. The only pauses are
 * the student's own, the OS's (a call, another media element on iOS) and other
 * in-app media claiming focus.
 */

export type AudioOverviewPlayerStatus =
  'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

/** A signed, expiring URL for the episode (`GET /audio-overviews/:id/audio`). */
export interface AudioOverviewSource {
  url: string;
  expiresAt: string;
}

export interface AudioOverviewTrack {
  id: string;
  title: string;
  subtitle?: string;
  sourceKind?: 'lecture' | 'article';
  articleId?: string | null;
  /** Known length before metadata loads, so the scrubber has a scale at once. */
  durationSec?: number;
  /** Called on load and again whenever the signed URL needs refreshing. */
  getSource: () => Promise<AudioOverviewSource>;
}

export interface AudioOverviewLoadOptions {
  /** Defaults to true. Call `load` from the tap handler so iOS allows it. */
  autoplay?: boolean;
  startAt?: number;
}

export const AUDIO_OVERVIEW_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;
export type AudioOverviewRate = (typeof AUDIO_OVERVIEW_RATES)[number];

export interface AudioOverviewPlayerState {
  status: AudioOverviewPlayerStatus;
  track: AudioOverviewTrack | null;
  currentTime: number;
  duration: number;
  /** End of the buffered range around the playhead, in seconds. */
  buffered: number;
  rate: AudioOverviewRate;
  error: string | null;
}

export interface AudioOverviewProgress {
  trackId: string;
  positionSec: number;
  completed: boolean;
}

/**
 * Receives resume positions (`PUT /audio-overviews/:id/progress`). It also
 * runs on `pagehide`, so an implementation should send with `keepalive`.
 */
export type AudioOverviewProgressHandler = (
  progress: AudioOverviewProgress
) => void;

export interface AudioOverviewPlayer {
  load(track: AudioOverviewTrack, options?: AudioOverviewLoadOptions): void;
  play(): void;
  pause(): void;
  toggle(): void;
  seekBy(deltaSec: number): void;
  seekTo(positionSec: number): void;
  setRate(rate: AudioOverviewRate): void;
  /** Stops playback and unloads the track. */
  close(): void;
  getState(): AudioOverviewPlayerState;
  subscribe(listener: () => void): () => void;
  setProgressHandler(handler: AudioOverviewProgressHandler | null): void;
  destroy(): void;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * The part of `navigator.mediaSession` the engine drives. The Android app
 * passes a plugin-backed stand-in (`nativeMediaSession.ts`).
 */
export type MediaSessionLike = Pick<
  MediaSession,
  'metadata' | 'playbackState' | 'setActionHandler' | 'setPositionState'
>;

export interface AudioOverviewPlayerDeps {
  /** Called once, on the first load; the element is reused for every track. */
  createAudio: () => HTMLAudioElement;
  getStorage: () => StorageLike | null;
  getMediaSession: () => MediaSessionLike | null;
  createMediaMetadata: (init: MediaMetadataInit) => MediaMetadata | null;
  /** Quiets every other in-app audio source before this one plays. */
  claimFocus: () => void;
  /**
   * Registers how other media (videos, chat read-aloud) quiet this player.
   * Optional so tests can leave the shared registry alone.
   */
  registerFocusOwner?: (yieldFocus: () => void) => () => void;
  onPageHide: (listener: () => void) => () => void;
  now: () => number;
  artwork: MediaImage[];
  onProgress?: AudioOverviewProgressHandler;
}

export const AUDIO_OVERVIEW_SKIP_SECONDS = 15;
export const AUDIO_OVERVIEW_PROGRESS_INTERVAL_MS = 15_000;
export const AUDIO_OVERVIEW_RATE_STORAGE_KEY = 'ora.audioOverview.rate';
export const AUDIO_OVERVIEW_ARTIST = 'Ora · Audio Overview';

/** On play, a signed URL this close to expiry is refreshed first. */
const REFRESH_LEAD_MS = 60_000;
/** Source refreshes allowed after media errors before giving up. */
const MAX_SOURCE_RETRIES = 2;
/** Clean playback past a recovery point that counts as recovered. */
const RECOVERY_SETTLE_SEC = 10;
const POSITION_STATE_INTERVAL_MS = 1_000;
const PLAY_ERROR = 'This episode could not be played. Try again.';
const LOAD_ERROR = 'This episode could not be loaded. Try again.';

/** A short silent WAV. Playing it inside the tap handler unlocks the element
 * on iOS before the signed URL arrives (same technique as `chatAudio.ts`). */
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

const HAVE_METADATA = 1;
const HAVE_FUTURE_DATA = 3;

const MEDIA_SESSION_ACTIONS: MediaSessionAction[] = [
  'play',
  'pause',
  'seekbackward',
  'seekforward',
  'seekto',
  'stop',
];

const isRate = (value: number): value is AudioOverviewRate =>
  (AUDIO_OVERVIEW_RATES as readonly number[]).includes(value);

const finiteOr = (value: number, fallback: number) =>
  Number.isFinite(value) && value > 0 ? value : fallback;

export const createAudioOverviewPlayer = (
  deps: AudioOverviewPlayerDeps
): AudioOverviewPlayer => {
  const listeners = new Set<() => void>();

  let progressHandler: AudioOverviewProgressHandler | null =
    deps.onProgress ?? null;

  let audio: HTMLAudioElement | null = null;
  /** False while the element holds nothing or the silent unlock clip. */
  let hasRealSource = false;
  /** Set while a refreshed URL replaces the old one mid-episode. */
  let isSwapping = false;
  /** What the student asked for; survives loading and URL refreshes. */
  let wantsPlay = false;
  let pendingSeek: number | null = null;
  let loadGeneration = 0;
  let sourceGeneration = 0;
  let sourceExpiresAtMs: number | null = null;
  /** Another source took the speaker: lock-screen controls are handed back. */
  let hasYieldedSession = false;
  let failures = 0;
  let recoveryPoint: number | null = null;
  let lastProgressAt = 0;
  let lastReported: AudioOverviewProgress | null = null;
  let lastPositionStateAt = 0;

  const readRate = (): AudioOverviewRate => {
    try {
      const stored = Number(
        deps.getStorage()?.getItem(AUDIO_OVERVIEW_RATE_STORAGE_KEY)
      );

      return isRate(stored) ? stored : 1;
    } catch {
      return 1;
    }
  };

  const idleState = (rate: AudioOverviewRate): AudioOverviewPlayerState => ({
    status: 'idle',
    track: null,
    currentTime: 0,
    duration: 0,
    buffered: 0,
    rate,
    error: null,
  });

  let state = idleState(readRate());

  const setState = (patch: Partial<AudioOverviewPlayerState>) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };

  // --- Media Session -------------------------------------------------------

  const mediaSession = () => {
    if (hasYieldedSession) return null;
    try {
      return deps.getMediaSession();
    } catch {
      return null;
    }
  };

  const setPlaybackState = (value: MediaSessionPlaybackState) => {
    const session = mediaSession();
    if (session) session.playbackState = value;
  };

  const updatePositionState = (force = false) => {
    const session = mediaSession();
    if (!session || typeof session.setPositionState !== 'function') return;
    const now = deps.now();
    if (!force && now - lastPositionStateAt < POSITION_STATE_INTERVAL_MS) {
      return;
    }
    if (!(state.duration > 0)) return;
    lastPositionStateAt = now;

    try {
      session.setPositionState({
        duration: state.duration,
        playbackRate: state.rate,
        position: Math.min(Math.max(0, state.currentTime), state.duration),
      });
    } catch {
      // Some engines reject position updates mid-seek; the next one lands.
    }
  };

  const setActionHandlers = (enabled: boolean) => {
    const session = mediaSession();
    if (!session) return;

    const handlers: Record<string, MediaSessionActionHandler> = {
      play: () => player.play(),
      pause: () => player.pause(),
      seekbackward: (details) =>
        player.seekBy(-(details.seekOffset ?? AUDIO_OVERVIEW_SKIP_SECONDS)),
      seekforward: (details) =>
        player.seekBy(details.seekOffset ?? AUDIO_OVERVIEW_SKIP_SECONDS),
      seekto: (details) => {
        if (typeof details.seekTime === 'number') {
          player.seekTo(details.seekTime);
        }
      },
      stop: () => player.close(),
    };

    MEDIA_SESSION_ACTIONS.forEach((action) => {
      try {
        session.setActionHandler(action, enabled ? handlers[action] : null);
      } catch {
        // Unsupported actions throw; the rest still register.
      }
    });
  };

  const setMetadata = (track: AudioOverviewTrack | null) => {
    const session = mediaSession();
    if (!session) return;

    try {
      session.metadata = track
        ? deps.createMediaMetadata({
            title: track.title,
            artist: AUDIO_OVERVIEW_ARTIST,
            album: track.subtitle ?? '',
            artwork: deps.artwork,
          })
        : null;
    } catch {
      // Metadata is a nicety; playback does not depend on it.
    }
  };

  // --- Progress ------------------------------------------------------------

  const reportProgress = (completed = false) => {
    const track = state.track;
    if (!track || !progressHandler) return;

    const position = completed
      ? finiteOr(state.duration, state.currentTime)
      : state.currentTime;

    const report: AudioOverviewProgress = {
      trackId: track.id,
      positionSec: Math.max(0, Math.floor(position)),
      completed,
    };

    lastProgressAt = deps.now();

    // Nothing listened yet, or nothing changed since the last report.
    if (!lastReported && report.positionSec === 0 && !completed) return;
    if (
      lastReported &&
      lastReported.trackId === report.trackId &&
      lastReported.positionSec === report.positionSec &&
      lastReported.completed === report.completed
    ) {
      return;
    }

    lastReported = report;
    try {
      progressHandler(report);
    } catch {
      // A failed save must never interrupt listening.
    }
  };

  // --- Element -------------------------------------------------------------

  const bufferedEnd = (element: HTMLAudioElement) => {
    const ranges = element.buffered;
    if (!ranges || ranges.length === 0) return 0;
    const time = element.currentTime;
    let furthest = 0;
    for (let index = 0; index < ranges.length; index += 1) {
      const end = ranges.end(index);
      if (ranges.start(index) <= time && time <= end) return end;
      furthest = Math.max(furthest, end);
    }
    return furthest;
  };

  const onLoadedMetadata = () => {
    if (!audio || !hasRealSource) return;
    isSwapping = false;
    if (pendingSeek !== null) {
      audio.currentTime = pendingSeek;
      pendingSeek = null;
    }
    setState({ duration: finiteOr(audio.duration, state.duration) });
    updatePositionState(true);
  };

  const onDurationChange = () => {
    if (!audio || !hasRealSource) return;
    setState({ duration: finiteOr(audio.duration, state.duration) });
  };

  const onTimeUpdate = () => {
    if (!audio || !hasRealSource || isSwapping || pendingSeek !== null) return;
    const currentTime = audio.currentTime;

    if (
      recoveryPoint !== null &&
      currentTime >= recoveryPoint + RECOVERY_SETTLE_SEC
    ) {
      failures = 0;
      recoveryPoint = null;
    }

    setState({ currentTime, buffered: bufferedEnd(audio) });
    updatePositionState();

    if (
      state.status === 'playing' &&
      deps.now() - lastProgressAt >= AUDIO_OVERVIEW_PROGRESS_INTERVAL_MS
    ) {
      reportProgress();
    }
  };

  const onBufferProgress = () => {
    if (!audio || !hasRealSource) return;
    setState({ buffered: bufferedEnd(audio) });
  };

  const onPlaying = () => {
    if (!hasRealSource) return;
    isSwapping = false;
    // The 15 s cadence counts from the moment sound starts.
    lastProgressAt = deps.now();
    setPlaybackState('playing');
    setState({ status: 'playing', error: null });
    updatePositionState(true);
  };

  const onWaiting = () => {
    if (!hasRealSource || !wantsPlay) return;
    setState({ status: 'loading' });
  };

  const onPause = () => {
    // A finished episode pauses first, then fires `ended`; that is not a
    // pause to report twice.
    if (!audio || !hasRealSource || audio.ended || isSwapping) return;
    wantsPlay = false;
    setPlaybackState('paused');
    setState({ status: 'paused', currentTime: audio.currentTime });
    reportProgress();
  };

  const onEnded = () => {
    if (!hasRealSource) return;
    wantsPlay = false;
    // Nothing is left to resume: on Android this also ends the notification.
    setPlaybackState('none');

    setState({
      status: 'ended',
      currentTime: finiteOr(state.duration, state.currentTime),
    });

    reportProgress(true);
  };

  const onError = () => {
    // Errors from the silent unlock clip or an emptied element are not ours.
    if (!hasRealSource) return;
    handleSourceFailure();
  };

  const ensureAudio = (): HTMLAudioElement => {
    if (audio) return audio;
    const element = deps.createAudio();
    element.preload = 'metadata';
    element.setAttribute('playsinline', '');

    (element as HTMLAudioElement & { playsInline?: boolean }).playsInline =
      true;

    element.addEventListener('loadedmetadata', onLoadedMetadata);
    element.addEventListener('durationchange', onDurationChange);
    element.addEventListener('timeupdate', onTimeUpdate);
    element.addEventListener('progress', onBufferProgress);
    element.addEventListener('playing', onPlaying);
    element.addEventListener('waiting', onWaiting);
    element.addEventListener('pause', onPause);
    element.addEventListener('ended', onEnded);
    element.addEventListener('error', onError);
    audio = element;
    return element;
  };

  /** Must run in the tap's call stack: iOS only unlocks media from there. */
  const unlockInGesture = () => {
    if (hasRealSource) return;
    const element = ensureAudio();
    element.src = SILENT_AUDIO;
    void Promise.resolve(element.play()).catch(() => undefined);
  };

  const startPlayback = () => {
    const element = audio;
    if (!element || !hasRealSource) return;
    deps.claimFocus();
    wantsPlay = true;
    if (element.readyState < HAVE_FUTURE_DATA) setState({ status: 'loading' });

    const generation = loadGeneration;

    void Promise.resolve(element.play()).catch((error: unknown) => {
      // A newer load or URL swap superseded this call.
      if ((error as { name?: string })?.name === 'AbortError') return;
      if (generation !== loadGeneration || !wantsPlay) return;
      // Usually autoplay policy: the student taps play themselves.
      wantsPlay = false;
      setPlaybackState('paused');
      setState({ status: 'paused' });
    });
  };

  const isSourceExpiring = () =>
    sourceExpiresAtMs !== null &&
    deps.now() >= sourceExpiresAtMs - REFRESH_LEAD_MS;

  const attachSource = (
    source: AudioOverviewSource,
    { startAt, play }: { startAt: number; play: boolean }
  ) => {
    const element = ensureAudio();
    // A seek made while the URL was loading beats the position asked for.
    const target = pendingSeek ?? startAt;
    const expiresAtMs = Date.parse(source.expiresAt);
    isSwapping = hasRealSource;
    hasRealSource = true;
    pendingSeek = target > 0 ? target : null;
    sourceExpiresAtMs = Number.isNaN(expiresAtMs) ? null : expiresAtMs;
    element.src = source.url;
    element.defaultPlaybackRate = state.rate;
    element.playbackRate = state.rate;
    setState({ currentTime: Math.max(0, target) });

    if (play) {
      startPlayback();
    } else if (state.status === 'loading' || state.status === 'playing') {
      setPlaybackState('paused');
      setState({ status: 'paused' });
    }
  };

  /**
   * Fetches a fresh signed URL and swaps it in at the current position. There
   * is no timer: a mid-episode swap rebuffers audibly, and the URL outlives
   * the episode. It runs only when needed:
   * - `resume`: `play` on a URL at or near expiry; on failure, try the old one.
   * - `replay`: `play` after the end, so the server checks access again; a
   *   refusal (e.g. the plan lapsed) is an error, never a retry loop.
   * - `error`: after a media error; counts toward the retry budget.
   * Whether to play afterwards is read when the URL arrives, so a pause (or
   * another source taking focus) during the fetch sticks.
   */
  const refreshSource = async (reason: 'resume' | 'replay' | 'error') => {
    const track = state.track;
    if (!track) return;

    const resumeAt = state.currentTime;
    const load = loadGeneration;
    const generation = ++sourceGeneration;
    let source: AudioOverviewSource;
    try {
      source = await track.getSource();
    } catch {
      if (load !== loadGeneration || generation !== sourceGeneration) return;
      if (reason === 'error') {
        handleSourceFailure();
      } else if (reason === 'replay') {
        wantsPlay = false;
        setPlaybackState('paused');
        setState({ status: 'error', error: LOAD_ERROR });
      } else if (wantsPlay) {
        startPlayback();
      }
      return;
    }

    if (load !== loadGeneration || generation !== sourceGeneration) return;
    if (reason === 'error') recoveryPoint = resumeAt;
    attachSource(source, { startAt: resumeAt, play: wantsPlay });
  };

  const handleSourceFailure = () => {
    if (failures >= MAX_SOURCE_RETRIES) {
      wantsPlay = false;
      setPlaybackState('paused');
      setState({ status: 'error', error: PLAY_ERROR });
      return;
    }
    failures += 1;
    // Keep showing progress while the new URL loads.
    if (wantsPlay || state.status === 'playing')
      setState({ status: 'loading' });
    void refreshSource('error');
  };

  const releaseSource = () => {
    loadGeneration += 1;
    sourceGeneration += 1;
    sourceExpiresAtMs = null;
    hasRealSource = false;
    isSwapping = false;
    wantsPlay = false;
    pendingSeek = null;
    failures = 0;
    recoveryPoint = null;
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
  };

  /** Hands the lock-screen controls back while other media plays. */
  const yieldFocus = () => {
    if (!state.track) return;
    player.pause();
    setActionHandlers(false);
    setMetadata(null);
    setPlaybackState('none');
    hasYieldedSession = true;
  };

  /** Takes the lock-screen controls back when this player plays again. */
  const reclaimSession = () => {
    if (!hasYieldedSession || !state.track) return;
    hasYieldedSession = false;
    setMetadata(state.track);
    setActionHandlers(true);
    updatePositionState(true);
  };

  const startLoad = (
    track: AudioOverviewTrack,
    { autoplay = true, startAt = 0 }: AudioOverviewLoadOptions
  ) => {
    if (state.track) reportProgress();
    releaseSource();
    lastReported = null;
    lastProgressAt = 0;
    hasYieldedSession = false;

    wantsPlay = autoplay;
    if (autoplay) {
      deps.claimFocus();
      unlockInGesture();
    }

    setMetadata(track);
    setActionHandlers(true);
    setPlaybackState(autoplay ? 'playing' : 'paused');

    setState({
      // `loading` always means "about to play"; a paused load is ready to tap.
      status: autoplay ? 'loading' : 'paused',
      track,
      currentTime: Math.max(0, startAt),
      duration: track.durationSec ?? 0,
      buffered: 0,
      error: null,
    });

    const generation = loadGeneration;

    track.getSource().then(
      (source) => {
        if (generation !== loadGeneration) return;

        attachSource(source, {
          startAt: Math.max(0, startAt),
          play: wantsPlay,
        });
      },
      () => {
        if (generation !== loadGeneration) return;
        wantsPlay = false;
        setPlaybackState('paused');
        setState({ status: 'error', error: LOAD_ERROR });
      }
    );
  };

  const player: AudioOverviewPlayer = {
    load(track, options = {}) {
      if (state.track?.id === track.id && state.status !== 'error') {
        if (typeof options.startAt === 'number') player.seekTo(options.startAt);
        if (options.autoplay !== false) player.play();
        return;
      }
      startLoad(track, options);
    },

    play() {
      const track = state.track;
      if (!track) return;
      reclaimSession();

      if (state.status === 'error') {
        startLoad(track, { autoplay: true, startAt: state.currentTime });
        return;
      }

      if (!hasRealSource) {
        wantsPlay = true;
        deps.claimFocus();
        unlockInGesture();
        setPlaybackState('playing');
        setState({ status: 'loading' });
        return;
      }

      const isReplay = state.status === 'ended';

      if (isReplay && audio) {
        audio.currentTime = 0;
        setState({ currentTime: 0 });
      }

      if (isReplay || isSourceExpiring()) {
        wantsPlay = true;
        deps.claimFocus();
        setPlaybackState('playing');
        setState({ status: 'loading' });
        void refreshSource(isReplay ? 'replay' : 'resume');
        return;
      }

      startPlayback();
    },

    pause() {
      if (!state.track) return;
      wantsPlay = false;

      if (hasRealSource && audio && !audio.paused && !isSwapping) {
        // The `pause` event updates state and reports progress.
        audio.pause();
        return;
      }

      // Still loading (or mid URL swap, whose events are ignored): stop the
      // unlock clip or the new source and settle the state here.
      audio?.pause();
      if (state.status === 'loading' || state.status === 'playing') {
        setPlaybackState('paused');
        setState({ status: 'paused' });
      }
    },

    toggle() {
      const isActive =
        state.status === 'playing' || (state.status === 'loading' && wantsPlay);

      if (isActive) player.pause();
      else player.play();
    },

    seekBy(deltaSec) {
      player.seekTo(state.currentTime + deltaSec);
    },

    seekTo(positionSec) {
      if (!state.track || !Number.isFinite(positionSec)) return;
      const max = state.duration > 0 ? state.duration : Infinity;
      const target = Math.min(Math.max(0, positionSec), max);

      if (hasRealSource && audio && audio.readyState >= HAVE_METADATA) {
        audio.currentTime = target;
      } else {
        pendingSeek = target;
      }

      const status =
        state.status === 'ended' && target < max ? 'paused' : state.status;

      setState({ currentTime: target, status });
      updatePositionState(true);
    },

    setRate(rate) {
      if (!isRate(rate)) return;
      if (audio) {
        audio.defaultPlaybackRate = rate;
        audio.playbackRate = rate;
      }
      try {
        deps
          .getStorage()
          ?.setItem(AUDIO_OVERVIEW_RATE_STORAGE_KEY, String(rate));
      } catch {
        // Private mode: the rate still applies for this session.
      }
      setState({ rate });
      updatePositionState(true);
    },

    close() {
      if (!state.track) return;
      reportProgress(state.status === 'ended');
      releaseSource();
      lastReported = null;
      setActionHandlers(false);
      setMetadata(null);
      setPlaybackState('none');
      hasYieldedSession = false;
      state = idleState(state.rate);
      listeners.forEach((listener) => listener());
    },

    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    setProgressHandler(handler) {
      progressHandler = handler;
    },

    destroy() {
      player.close();
      removePageHide();
      releaseFocusOwner();
      if (!audio) return;
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('progress', onBufferProgress);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio = null;
    },
  };

  // The tab or app may be discarded without another chance to save.
  const removePageHide = deps.onPageHide(() => {
    if (state.track) reportProgress(state.status === 'ended');
  });

  const releaseFocusOwner =
    deps.registerFocusOwner?.(yieldFocus) ?? (() => undefined);

  return player;
};

const APP_ARTWORK: MediaImage[] = [
  { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
  { src: '/android-chrome-512x512.png', sizes: '512x512', type: 'image/png' },
];

/** Where yielding to other media lands for the app-wide player. */
let yieldDefaultPlayer: (() => void) | null = null;

/** Plain metadata for the native session, which only reads the fields. */
const toPlainMetadata = (init: MediaMetadataInit): MediaMetadata => ({
  title: init.title ?? '',
  artist: init.artist ?? '',
  album: init.album ?? '',
  artwork: init.artwork ?? [],
});

/**
 * The real browser wiring, exported so tests can exercise it. On the Android
 * app with the media-session plugin, the lock-screen controls go through the
 * plugin instead of `navigator.mediaSession`.
 *
 * The app-wide player is created at startup for every user, so the plugin is
 * picked (and first called) only when the engine first needs a session: an
 * episode load. The one exception is a reload after a page that left the
 * plugin's service running, which is cleared right away.
 */
export const createBrowserAudioOverviewDeps = (): AudioOverviewPlayerDeps => {
  let nativeSession: MediaSessionLike | null | undefined;

  const getNativeSession = (): MediaSessionLike | null => {
    if (nativeSession === undefined) nativeSession = createNativeMediaSession();
    return nativeSession;
  };

  if (hasNativeSessionMarker(getNativeSessionMarkerStorage())) {
    getNativeSession();
  }

  return {
    createAudio: () => new Audio(),
    getStorage: () => {
      try {
        return window.localStorage;
      } catch {
        return null;
      }
    },
    getMediaSession: () => {
      const nativeSession = getNativeSession();
      if (nativeSession) return nativeSession;
      return typeof navigator !== 'undefined' && 'mediaSession' in navigator
        ? navigator.mediaSession
        : null;
    },
    createMediaMetadata: (init) => {
      if (getNativeSession()) return toPlainMetadata(init);
      return typeof MediaMetadata === 'function'
        ? new MediaMetadata(init)
        : null;
    },
    claimFocus: () => claimMediaFocus('audio-overview'),
    onPageHide: (listener) => {
      window.addEventListener('pagehide', listener);
      return () => window.removeEventListener('pagehide', listener);
    },
    now: () => Date.now(),
    artwork: APP_ARTWORK,
  };
};

let defaultPlayer: AudioOverviewPlayer | null = null;

/** The app-wide player. Creating it is cheap: no media element until a load. */
export const getAudioOverviewPlayer = (): AudioOverviewPlayer => {
  if (defaultPlayer) return defaultPlayer;

  const player = createAudioOverviewPlayer({
    ...createBrowserAudioOverviewDeps(),
    registerFocusOwner: (yieldFocus) => {
      yieldDefaultPlayer = yieldFocus;
      return registerMediaFocusOwner('audio-overview', yieldFocus);
    },
  });

  defaultPlayer = player;
  // An episode belongs to the signed-in account: logout stops and unloads it.
  registerChatSessionClearer(() => player.close());
  return player;
};

/**
 * For other media that start playing: pauses the episode and hands the
 * lock-screen controls back until it plays again. A no-op before any load.
 */
export const pauseAudioOverview = (): void => {
  yieldDefaultPlayer?.();
};

export const loadAudioOverview = (
  track: AudioOverviewTrack,
  options?: AudioOverviewLoadOptions
): void => getAudioOverviewPlayer().load(track, options);

export const setAudioOverviewProgressHandler = (
  handler: AudioOverviewProgressHandler | null
): void => getAudioOverviewPlayer().setProgressHandler(handler);
